import { type PagamentoWithDettaglioDTO } from '@framework/api-client/api/inquiries-compile/schemas/pagamentoWithDettaglioDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { SummaryDetailCard } from '@framework/ui/components/summary/SummaryDetailCard.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DeletePaymentButton } from '../../../../payments/buttons/DeletePaymentButton.tsx';
import { DownloadPaymentAlertButton } from '../../../../payments/buttons/DownloadPaymentAlertButton.tsx';
import { DownloadPaymentReceiptButton } from '../../../../payments/buttons/DownloadPaymentReceiptButton.tsx';
import { StartPaymentButton } from '../../../../payments/buttons/StartPaymentButton.tsx';
import { StartStampDutyPaymentButton } from '../../../../payments/buttons/StartStampDutyPayment.tsx';
import { SummaryPaymentBurdenTable } from './burden-table/SummaryPaymentBurdenTable.tsx';
import {
  getPaymentRowClassName,
  PaymentsTableWrapper,
  summaryPaymentsColumns,
} from './summaryPaymentsColumns.tsx';
import { useExtendedPaymentsTable } from './useExtendedPaymentsTable.ts';

// Gli oneri sono figli del pagamento: come nella Cronologia (fase ->
// attività/eventi), il dettaglio rientra dello spazio del controllo di
// espansione, allineandosi alla prima colonna dati del pagamento
// (24px + padding cella = larghezza della colonna expander)
const BurdenRowIndent = styled.div`
  padding-left: 24px;
`;

export interface IExtendedPaymentsTableProps {
  payments: PagamentoWithDettaglioDTO[];
  submission: RichiestaDTO;
  /** Se true, nasconde la colonna Azioni (es. consultazione dal Backoffice) */
  readonly?: boolean;
}

export function ExtendedPaymentsTable(props: IExtendedPaymentsTableProps) {
  const { payments, submission, readonly = false } = props;
  const { t } = useTranslation();

  const { expandedKeys, toggleRowExpand, expandableConfig } =
    useExtendedPaymentsTable();

  const expandedRowRender = useCallback(
    (record: PagamentoWithDettaglioDTO) => (
      <BurdenRowIndent>
        <SummaryPaymentBurdenTable burdens={record.oneri ?? []} />
      </BurdenRowIndent>
    ),
    [],
  );

  return (
    <PaymentsTableWrapper>
      <SummaryDetailCard noPadding>
        <TableData
          removeShadow
          dataSource={payments}
          columns={[
            ...summaryPaymentsColumns,
            {
              title: t('summary-payments.azioni', 'Azioni'),
              key: 'actions',
              align: 'center',
              hidden: readonly,
              render: (_, record) => (
                <Space onClick={e => e.stopPropagation()}>
                  <DeletePaymentButton payment={record} />
                  <DownloadPaymentAlertButton
                    payment={record}
                    type="link"
                    size="small"
                  />
                  <DownloadPaymentReceiptButton
                    payment={record}
                    type="link"
                    size="small"
                  />
                  <StartPaymentButton
                    payment={record}
                    type="link"
                    size="small"
                  />
                  <StartStampDutyPaymentButton
                    payment={record}
                    submission={submission}
                    type="link"
                    size="small"
                  />
                </Space>
              ),
            },
          ]}
          rowKey="id"
          pagination={false}
          size="middle"
          rowClassName={record => getPaymentRowClassName(record, expandedKeys)}
          onRow={record => ({
            onClick: () => toggleRowExpand(record),
          })}
          expandable={{ ...expandableConfig, expandedRowRender }}
        />
      </SummaryDetailCard>
    </PaymentsTableWrapper>
  );
}
